import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MagnifyingGlassIcon, CpuChipIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { startArticleSearch, getSearchStatus, getSearchResults } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import { sanitizeUserInput } from '../../utils/security'
import NoCreditsModal from '../Billing/NoCreditsModal'
import ErrorPopup from '../PopupNotifications/ErrorPopup'
import KeywordsAutocomplete from './KeywordsAutocomplete'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'

function ArticleSearchForm({ credits, onSearchComplete, loading, setLoading }) {
    const { t } = useTranslation()
    const { setCredits } = useMyContext()
    const [query, setQuery] = useState('')
    const [publicationYearFilter, setPublicationYearFilter] = useState('')
    const [sortOption, setSortOption] = useState('relevance_score:desc')
    const [articleLanguage, setArticleLanguage] = useState('')
    const [selectedKeywords, setSelectedKeywords] = useState([])
    const [insufficientCreditsModal, setInsufficientCreditsModal] = useState(null)
    const [errorMessage, setErrorMessage] = useState('')

    const SEARCH_COST = 2

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!query.trim()) {
            setErrorMessage(t('articleBibliography.errors.queryRequired'))
            return
        }

        if (credits < SEARCH_COST) {
            setInsufficientCreditsModal(
                <NoCreditsModal
                    title={t('CreditsModal.Title')}
                    body={t('CreditsModal.Body')}
                    continue={t('CreditsModal.Continue')}
                    navLink="/pricing/credits"
                    handleClose={() => setInsufficientCreditsModal(null)}
                />
            )
            return
        }

        try {
            setLoading(true)
            
            // Immediately deduct credits from the UI
            setCredits(prevCredits => prevCredits - SEARCH_COST)

            // Prepare search data
            const searchData = {
                query: `"${sanitizeUserInput(query.trim())}"`,
                team_uuid: getTeamUuid(),
                page: 1,
                per_page: 25,
                sort: sortOption
            }

            // Add filters if specified
            const filters = { type: "article" }
            if (publicationYearFilter) {
                filters.publication_year = publicationYearFilter
            }
            
            if (Object.keys(filters).length > 0) {
                searchData.filters = filters
            }
            
            // Add language at root level if specified (ISO 639 code)
            if (articleLanguage) {
                searchData.language = articleLanguage
            }
            
            // Add keywords at root level if selected (send keyword IDs)
            if (selectedKeywords.length > 0) {
                searchData.keywords = selectedKeywords.map(keyword => keyword.id)
            }

            // Start the search
            const searchResponse = await startArticleSearch(searchData)
            const searchRequestId = searchResponse.data.search_request_id

            // Poll for search completion
            const pollForResults = async () => {
                let attempts = 0
                const maxAttempts = 30 // 30 * 2 seconds = 1 minute timeout

                const checkStatus = async () => {
                    try {
                        const statusResponse = await getSearchStatus(searchRequestId)
                        const status = statusResponse.data.status

                        if (status === 'completed') {
                            // Fetch results
                            const resultsResponse = await getSearchResults(searchRequestId)
                            onSearchComplete(statusResponse.data, resultsResponse.data)
                            setLoading(false)
                        } else if (status === 'failed') {
                            throw new Error(statusResponse.data.error_message || t('articleBibliography.errors.searchFailed'))
                        } else if (attempts < maxAttempts) {
                            attempts++
                            setTimeout(checkStatus, 2000) // Check again in 2 seconds
                        } else {
                            throw new Error(t('articleBibliography.errors.searchTimeout'))
                        }
                    } catch (error) {
                        setLoading(false)
                        // Re-add credits on error
                        setCredits(prevCredits => prevCredits + SEARCH_COST)
                        setErrorMessage(error.response?.data?.message || error.message || t('articleBibliography.errors.searchFailed'))
                    }
                }

                checkStatus()
            }

            pollForResults()

        } catch (error) {
            setLoading(false)
            // Re-add credits on error
            setCredits(prevCredits => prevCredits + SEARCH_COST)
            console.error('Error starting article search:', error)
            if (error.response?.data?.message === 'Insufficient credits') {
                setInsufficientCreditsModal(
                    <NoCreditsModal
                        title={t('CreditsModal.Title')}
                        body={t('CreditsModal.Body')}
                        continue={t('CreditsModal.Continue')}
                        navLink="/pricing/credits"
                        handleClose={() => setInsufficientCreditsModal(null)}
                    />
                )
            } else {
                setErrorMessage(error.response?.data?.message || t('articleBibliography.errors.searchFailed'))
            }
        }
    }

    const publicationYearOptions = [
        { value: '', label: t('articleBibliography.search.filters.allYears') },
        { value: '2024', label: '2024' },
        { value: '2023-2024', label: '2023-2024' },
        { value: '2020-2024', label: '2020-2024' },
        { value: '2015-2024', label: '2015-2024' },
        { value: '2010-2024', label: '2010-2024' }
    ]

    const sortOptions = [
        { value: 'cited_by_count:desc', label: t('articleBibliography.search.sort.mostCited') },
        { value: 'publication_date:desc', label: t('articleBibliography.search.sort.newest') },
        { value: 'publication_date:asc', label: t('articleBibliography.search.sort.oldest') },
        { value: 'relevance_score:desc', label: t('articleBibliography.search.sort.relevance') }
    ]

    // Use the same language codes as audio upload feature
    const languageCodes = [
        "pt", "es", "en", "en_au", "en_uk", "en_us", "fr", "de", "it", "nl", "af", "sq", "am",
        "ar", "hy", "as", "az", "ba", "eu", "be", "bn", "bs", "br", "bg", "my", "ca", "zh",
        "hr", "cs", "da", "et", "fo", "fi", "gl", "ka", "el", "gu", "ht", "ha", "haw", "he",
        "hi", "hu", "is", "id", "ja", "jw", "kn", "kk", "km", "ko", "lo", "la", "lv", "ln",
        "lt", "lb", "mk", "mg", "ms", "ml", "mt", "mi", "mr", "mn", "ne", "no", "nn", "oc",
        "pa", "ps", "fa", "pl", "ro", "ru", "sa", "sr", "sn", "sd", "si", "sk", "sl", "so",
        "su", "sw", "sv", "tl", "tg", "ta", "tt", "te", "th", "bo", "tr", "tk", "uk", "ur",
        "uz", "vi", "cy", "yi", "yo"
    ];

    const languageOptions = [
        { value: '', label: t('articleBibliography.search.language.anyLanguage') },
        ...languageCodes.map(code => ({
            value: code,
            label: t(`languages.${code}`)
        }))
    ]

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col space-y-4">
                    {/* Search Query */}
                    <div>
                        <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('articleBibliography.search.query')}
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="search-query"
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-black focus:border-black"
                                placeholder={t('articleBibliography.search.placeholder')}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {/* Keywords Autocomplete */}
                    <KeywordsAutocomplete
                        selectedKeywords={selectedKeywords}
                        onKeywordsChange={setSelectedKeywords}
                        disabled={loading}
                    />

                    {/* Warning about English-only tags */}
                    <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            {t('articleBibliography.search.warning.englishOnlyTags')}
                        </p>
                    </div>

                    {/* Filters Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Publication Year Filter */}
                        <div>
                            <label htmlFor="publication-year" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('articleBibliography.search.filters.publicationYear')}
                            </label>
                            <select
                                id="publication-year"
                                value={publicationYearFilter}
                                onChange={(e) => setPublicationYearFilter(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-black focus:border-black"
                                disabled={loading}
                            >
                                {publicationYearOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Sort Option */}
                        <div>
                            <label htmlFor="sort-option" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('articleBibliography.search.sort.label')}
                            </label>
                            <select
                                id="sort-option"
                                value={sortOption}
                                onChange={(e) => setSortOption(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-black focus:border-black"
                                disabled={loading}
                            >
                                {sortOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Article Language Filter */}
                        <div>
                            <label htmlFor="article-language" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('articleBibliography.search.language.label')}
                            </label>
                            <select
                                id="article-language"
                                value={articleLanguage}
                                onChange={(e) => setArticleLanguage(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-black focus:border-black"
                                disabled={loading}
                            >
                                {languageOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Search Button */}
                    <div className="flex justify-center">
                        <button
                            type="submit"
                            disabled={loading || !query.trim() || credits < SEARCH_COST}
                            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    {t('articleBibliography.search.searching')}
                                </div>
                            ) : (
                                <div className='flex justify-between items-center gap-3 w-full'>
                                    <div className="flex items-center gap-2">
                                        <MagnifyingGlassIcon className="h-4 w-4" />
                                        {t('articleBibliography.search.button')}
                                    </div>
                                    <div className='flex items-center gap-1'>
                                        <div className='font-bold'>{SEARCH_COST.toFixed(2)}</div>
                                        <CpuChipIcon className='w-5 h-5' />
                                    </div>
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {insufficientCreditsModal && insufficientCreditsModal}

            {errorMessage && (
                <ErrorPopup
                    message={errorMessage}
                    onClose={() => setErrorMessage('')}
                />
            )}
        </>
    )
}

export default ArticleSearchForm
